import { useMemo } from "react";
import { Chats } from "../../features/chat/domain/entities/getChat";
import { determineCurrentStatus, formatDate } from "../constants/constants";
import { CameraPublic } from "../icons/Camera";
import { ReadingMessage, ReadMessage } from "../icons/Read";
import { Column } from "./Column";
import { Row } from "./Row";
import { TextComponent } from "./TextComponent";
import { useLocation } from "react-router-dom";
import { User } from "../icons/User";
import { TimePending } from "../icons/Time";

export const Chat = (data: Chats) => {
	const location = useLocation();
	const isActive = location.pathname.endsWith(data.user_id);
	const chatStatus = useMemo(() => determineCurrentStatus(data), [data]);
	let statusMessage = useMemo(() => {
		switch (chatStatus) {
			case "banned":
			case "inactive":
				return `${data.username} no longer exists`;
			case "blocked":
				return data.status_user_id2 === "blocked" ? `You blocked ${data.username}` : `${data.username} has blocked you`;
			case "leaved":
				return data.status_user_id2 === "leaved" ? `You left the conversation with ${data.username}` : `${data.username} has left the conversation`;
			case "deleted":
				return `${data.username} has deleted the match`;
			default:
				return `${data.username} is active.`;
		}
	}, [chatStatus, data]);

	return (
		<Row
			className={`${isActive && "bg-grayDefault"} max-md:border-b max-md:border-grayLight lg:border-b lg:border-grayLight p-2.5 lg:px-6 lg:py-4 hover:bg-grayDefault cursor-pointer select-none`}
			crossAxisAlignment="center"
		>
			<Row
				className="gap-3"
				crossAxisAlignment="center"
			>
				{data.profile_pictures ?
					<img
						className="w-12 aspect-square object-cover rounded-full"
						src={data.profile_pictures[0]}
						draggable={false}
						alt={`profile-${data.username}`}
					/>
					:
					<User className="bg-gray-200 p-2 text-white w-12 aspect-square object-cover rounded-full" />
				}
				<Column className="md:hidden lg:flex">
					<TextComponent
						variant="nh5"
						className="font-semibold"
					>
						{data.username}
					</TextComponent>
					<Row
						crossAxisAlignment="center"
						className="gap-0.5 max-w-52"
					>
						{chatStatus === "active" ? (
							<>
								{data.user_id !== data.message_sender_id && data.message_text && (
									<TextComponent
										className={`flex text-graySecondary`}
										variant="nh8"
									>
										
										{data.read ? <ReadMessage color="currentColor" /> : (data.loading ? <TimePending className="w-3 md:w-4" /> : <ReadingMessage color="currentColor" />)}
									</TextComponent>
								)}
								{(data.type === "image" || data.type === "file") ? (
									<Row
										crossAxisAlignment="center"
										className="gap-0.5 text-graySecondary"
									>
										<CameraPublic className="w-3 md:w-4" />
										<TextComponent variant="nh7">photo</TextComponent>
									</Row>
								) : (
									<TextComponent
										variant="nh7"
										className="text-graySecondary truncate"
									>
										{data.message_text}
									</TextComponent>
								)}
							</>
						) : (
							<>
								<TextComponent
									variant="nh7"
									className="text-graySecondary truncate"
								>
									{statusMessage}
								</TextComponent>
							</>
						)}
					</Row>
				</Column>
			</Row>
			<Column
				crossAxisAlignment="end"
				mainAxisAlignment="center"
				className="md:hidden lg:flex max-w-16 gap-[5px]"
			>
				<TextComponent
					variant="nh8"
					className="text-graySecondary leading-3 max-w-16 truncate"
				>
					{formatDate(data.sent_at)}
				</TextComponent>
				{data.unread_messages !== undefined && data.unread_messages > 0 && (
					<TextComponent
						variant="nh8"
						className="flex items-center justify-center text-white bg-primary min-w-5 h-5 px-1 rounded-full"
					>
						{data.unread_messages > 999 ? "+999" : data.unread_messages}
					</TextComponent>
				)}
			</Column>
		</Row>
	);
};
